import { DecoratedFormProps, FormErrors, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { Dispatch, useMemo, useEffect } from 'react';
import { maxLength, required, Validators } from 'src/components/redux-form/utils/validators';

import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from './assets/contract-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { push } from 'connected-react-router';
import { toast } from 'react-toastify';
import { asyncPersistContract } from 'src/store/actions/contract-actions';
import { FieldDropdown } from '../../../../../components/redux-form/fields/FieldDropdown';
import { getValFromEnum } from '../../../../../components/redux-form/utils/dropdown-values';
import GeneralEnum from '../../../../../enumerables/general-enum';
import { FieldMultiSelect } from '../../../../../components/redux-form/fields/FieldMultiSelect';

export interface ContractFormData {
    id: string;
    student: string;
    languageDropDown: any;
    caseNumberDropDown: any;
    orders?: Array<{ value: number | string; label: string }>;
}
export interface ContractSendFormData {
    id: string | number;
    statusDocusign: number;
    student: any;
}
interface ContractFormProps {
    caseNumbersStudent: Array<{
        caseNumber: string;
        orders: Array<{ id: number | string; name: string }>;
    }>;
    selectedCase?: string;
}

type InjectedProps = InjectedFormProps<ContractFormData, ContractFormProps>;

// validators.ts
export const requiredArray = (msg = 'Select at least one order.') => (value: any) =>
    Array.isArray(value) && value.length > 0 ? undefined : msg;

const validators: Validators<ContractFormData> = {
    languageDropDown: [required({ message: 'Language is required.' }), maxLength({ max: 255 })],
    caseNumberDropDown: [required({ message: 'Case number is required.' }), maxLength({ max: 255 })],
    orders: [requiredArray('Select at least one order.')],
};

const ContractForm: React.FunctionComponent<InjectedProps & ContractFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    caseNumbersStudent,
    selectedCase,
    change,
}: InjectedProps & ContractFormProps) => {
    const isNew = !initialValues || !initialValues.id;

    const caseNumbersValues: SelectObject[] = useMemo(() => {
        return (caseNumbersStudent || []).map((prov) => ({
            value: prov.caseNumber,
            label: String(prov.caseNumber),
        }));
    }, [caseNumbersStudent]);

    const getSelectedCaseValue = (val: any): string | undefined => {
        if (val == null) return undefined;
        return typeof val === 'object' ? val.value : String(val);
    };

    const selectedCaseValue = getSelectedCaseValue(selectedCase);
    const isCaseSelected = !!selectedCaseValue;

    const orderOptions = useMemo(() => {
        if (!selectedCase) return [];
        const item = (caseNumbersStudent || []).find((x) => String(x.caseNumber) === String(selectedCase));
        if (!item) return [];
        const c = item.orders.map((o) => ({ value: o.id, label: o.name }));
        return item.orders.map((o) => ({ value: o.id, label: 'ID=' + o.id + ': ' + o.name }));
    }, [selectedCase, caseNumbersStudent]);

    useEffect(() => {
        change('orders', []);
        change('languageDropDown', undefined);
    }, [selectedCaseValue]);

    const langEnum = GeneralEnum ? GeneralEnum.getEnum('ContractLanguage') : {};
    const langValues: SelectObject[] = getValFromEnum(langEnum);

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />
            <FieldInput
                name="id"
                type="hidden"
                wrapperConfig={{
                    classes: {
                        inputWrapper: 'm-0',
                    },
                }}
            />
            <FieldDropdown
                name="caseNumberDropDown"
                data={caseNumbersValues}
                textField={'label'}
                valueField={'value'}
                parse={(option: any) => (option && typeof option === 'object' ? option.value : option)}
                format={(value: any) => (value == null ? value : { value, label: String(value) })}
                validate={validators.caseNumberDropDown}
                wrapperConfig={{
                    label: 'Case Number: *',
                }}
            />

            {isCaseSelected && (
                <>
                    <div className={'rw-dropdown-list rw-widget'}>
                        <FieldMultiSelect
                            name="orders"
                            validate={validators.orders}
                            options={orderOptions}
                            label="Orders:"
                            description="You can select more than one order"
                        />
                    </div>
                    <FieldDropdown
                        name="languageDropDown"
                        data={langValues}
                        textField={'label'}
                        valueField={'value'}
                        validate={validators.languageDropDown}
                        parse={(option: any) => (option && typeof option === 'object' ? option.value : option)}
                        format={(value: any) => (value == null ? value : { value, label: String(value) })}
                        wrapperConfig={{
                            label: 'Language: *',
                        }}
                    />
                </>
            )}

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Generate
                </button>
                <Link
                    className={classNames('btn', 'btn-danger')}
                    to={Router.generate(routes.STUDENT_DETAIL, { id: initialValues.student, tab: 'contracts' })}
                >
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

const formName = 'contract-form';

// create a selector for this form
const selector = formValueSelector(formName);

// connect to redux state
const ConnectedContractForm = connect((state: any) => {
    return {
        selectedCase: selector(state, 'caseNumberDropDown'), // take the selected value
    };
})(ContractForm);

// wrap reduxForm around connect
export default reduxForm<ContractFormData, ContractFormProps>({
    form: formName,
    validate(
        values: ContractFormData,
        props: DecoratedFormProps<ContractFormData, ContractFormProps, string>,
    ): FormErrors<ContractFormData, string> {
        const errors: FormErrors<ContractFormData, string> = {};
        return errors;
    },
    onSubmit: (values, dispatch, props) => {
        const payload = {
            student: values.student,
            language: values.languageDropDown,
            caseNumber: values.caseNumberDropDown,
            orders: (values.orders || []).map((o: any) => o.value),
        };
        console.log(payload);

        //return dispatch(asyncPersistContract(payload, dispatch));
        return asyncPersistContract(payload, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<ContractFormData, ContractFormProps, string>,
    ) {
        const isNew = !props.initialValues || !props.initialValues.id;
        toast.success(`Contract was successfully ${isNew ? 'created' : 'updated'}.`);
        dispatch(push(Router.generate(routes.STUDENT_DETAIL, { id: props?.initialValues?.student, tab: 'contracts' })));
    },
    onSubmitFail(errors: { _error: string }) {
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(ConnectedContractForm);
